/**
 * The game loop: the code that keeps the game running, frame after frame.
 *
 * Browsers redraw the screen about 60 times a second, and
 * `requestAnimationFrame` asks the browser to call us just before each redraw.
 * But screens differ (some run at 120 or 144 frames a second), and frames are
 * sometimes late. If the game moved things by "one step per frame", it would
 * run at different speeds on different machines.
 *
 * So this loop uses a "fixed timestep": it keeps track of how much real time
 * has passed and runs the game's update in equal slices (1/60 of a second by
 * default), as many as needed to catch up. Drawing still happens once per
 * frame. The game then behaves the same on every machine.
 */
/** The two functions the loop calls. */
export interface LoopCallbacks {
	/**
	 * Advance the game by `dt` seconds. May be called several times in one
	 * frame (to catch up) or not at all (if not enough time has passed yet).
	 */
	update: (dt: number) => void;
	/**
	 * Draw the current state. Called once per frame. `alpha` (0 to 1) says how
	 * far real time is between the last update and the next one, which a game can
	 * use to draw smoother motion. This game doesn't need it.
	 */
	render: (alpha: number) => void;
}

/** Controls for a loop: `start()` begins calling the callbacks, `stop()` ends it. */
export interface Loop {
	start: () => void;
	stop: () => void;
}

/**
 * The most real time (in seconds) handled in one frame. If the tab was in the
 * background for a minute, the game shouldn't try to simulate that whole
 * minute in one go; it just carries on from where it was.
 */
const MAX_FRAME_TIME = 0.25;

/**
 * Create a fixed-timestep loop (see the explanation at the top of this file).
 * Nothing happens until `start()` is called.
 *
 * The variables declared inside (`last`, `accumulator`, ...) live on between
 * frames because the inner functions refer to them. This is called a
 * "closure", and it's a common way to keep private state without a class.
 */
export function createLoop(
	{ update, render }: LoopCallbacks,
	stepSeconds = 1 / 60,
): Loop {
	let rafId: number | null = null;
	let last = 0;
	let accumulator = 0;

	const frame = (now: number) => {
		const frameTime = Math.min((now - last) / 1000, MAX_FRAME_TIME);
		last = now;
		accumulator += frameTime;
		while (accumulator >= stepSeconds) {
			update(stepSeconds);
			accumulator -= stepSeconds;
		}
		render(accumulator / stepSeconds);
		rafId = requestAnimationFrame(frame);
	};

	return {
		start() {
			if (rafId !== null) return;
			last = performance.now();
			accumulator = 0;
			rafId = requestAnimationFrame(frame);
		},
		stop() {
			if (rafId === null) return;
			cancelAnimationFrame(rafId);
			rafId = null;
		},
	};
}
